import { TMableEvent } from '@/core/constants/event_types/base_mable_event';
import { EMableEventName } from '@/core/constants/event_types/events';
import { TMableProduct } from '@/core/constants/event_types/extended_mable_event';
import { Logger } from '@/shared/logger/logger';

import {
  calculateGA4EventMapping,
  GA4Event,
  GA4EventParameters,
  GA4Item,
} from './types';

/**
 * Helper function to map cart items to GA4 items format
 * @param cart The cart object
 * @returns Array of GA4 formatted items
 */
function mapCartItemsToGA4Items(cart: any): GA4Item[] {
  if (!cart.li || !Array.isArray(cart.li)) {
    return [];
  }

  return cart.li.map((item: any) => ({
    currency: cart.cu,
    discount: item.discount || 0,
    item_brand: item.br,
    item_category: item.ca?.[0],
    item_category2: item.ca?.[1],
    item_category3: item.ca?.[2],
    item_category4: item.ca?.[3],
    item_category5: item.ca?.[4],
    item_id: item.id,
    item_name: item.nm,
    item_variant: item.va,
    price: item.pr,
    quantity: item.qt,
  }));
}

/**
 * Helper function to create a GA4 item from a Mable product
 * @param product The Mable product
 * @returns GA4 formatted item
 */
function createGA4Item(product: TMableProduct): GA4Item {
  return {
    coupon: product.discounts?.[0]?.code || '',
    currency: product.cur || 'USD',
    discount: product.totalDiscount || 0,
    item_brand: product.br || '',
    item_category: product.cat || '',
    item_id: product.id || '',
    item_list_id: product.ids?.vid || '',
    item_list_name: product.var || '',
    item_name: product.nam || '',
    item_variant: product.var || '',
    price: Number.parseFloat(product.prc) || 0,
    quantity: product.qty || 1,
  };
}

/**
 * Transforms a Mable event to a GA4 event format
 * @param mableEvent The Mable event to transform
 * @returns GA4 formatted event
 */
export function transformMableEventToGA4Event(
  mableEvent: TMableEvent,
): GA4Event {
  const { en: eventName, esd, ii, pd, sd } = mableEvent;

  // Get the GA4 event name using our mapping function
  let ga4EventName = calculateGA4EventMapping(eventName, esd?.cen);

  const eventParams: GA4EventParameters = {
    // Common parameters
    page_location: pd.dl || window.location.href,
    page_referrer: document.referrer || '',
    page_title: document.title,
    user_id: ii || undefined,
  };

  // Add UTM parameters if available
  if (sd) {
    if (sd.iqpar?.['utm_source'])
      eventParams.utm_source = sd.iqpar?.['utm_source'][0];
    if (sd.iqpar?.['utm_medium'])
      eventParams.utm_medium = sd.iqpar?.['utm_medium'][0];
    if (sd.iqpar?.['utm_campaign'])
      eventParams.utm_campaign = sd.iqpar?.['utm_campaign'][0];
    if (sd.iqpar?.['utm_term'])
      eventParams.utm_term = sd.iqpar?.['utm_term'][0];
    if (sd.iqpar?.['utm_content'])
      eventParams.utm_content = sd.iqpar?.['utm_content'][0];
  }

  // Process event-specific data
  try {
    switch (eventName) {
      case EMableEventName.PAGE_VIEWED: {
        // ga4Event.event_name = 'page_view';
        break;
      }
      case EMableEventName.ORDER_COMPLETED: {
        // ga4Event.event_name = 'purchase';
        break;
      }
      case EMableEventName.POST_PURCHASE_SURVEY: {
        // ga4Event.event_name = 'post_purchase_survey';
        break;
      }
      case EMableEventName.PRODUCT_ADDED: {
        if (esd?.act_product) {
          const product = esd.act_product;
          eventParams.currency = product.cur;
          eventParams.value =
            Number.parseFloat(product.prc) * (esd?.qty_act_product || 1);
          eventParams.items = [createGA4Item(product)];
        }
        break;
      }

      case EMableEventName.CHECKOUT_STARTED: {
        if (esd?.cart) {
          const cart = esd.cart;
          eventParams.currency = cart.currency;
          eventParams.value = Number.parseFloat(cart.total.price.toString());
          eventParams.coupon = cart?.discounts?.[0]?.code;
          eventParams.items = cart.products?.map(createGA4Item) || [];
        }
        break;
      }

      case EMableEventName.PAYMENT_INFO_ADDED: {
        if (esd?.cart) {
          const cart = esd.cart;
          eventParams.currency = cart.currency;
          eventParams.value = Number.parseFloat(cart.total.price.toString());
          eventParams.items = cart.products?.map(createGA4Item) || [];
          eventParams.coupon = cart?.discounts?.[0]?.code;
          if (esd.pay_methods && esd.pay_methods.length > 0) {
            eventParams.payment_type = esd.pay_methods[0];
          }
        }
        break;
      }

      case EMableEventName.PRODUCT_VIEWED: {
        if (esd?.act_product) {
          const product = esd.act_product;
          eventParams.currency = product.cur;
          eventParams.value = Number.parseFloat(product.prc);
          eventParams.items = [createGA4Item(product)];
        }
        break;
      }

      case EMableEventName.COLLECTION_VIEWED: {
        if (esd?.['collection'] && esd?.['products']) {
          const collection = esd?.['collection'];
          eventParams.item_list_id = collection?.id;
          eventParams.item_list_name = collection?.name;
          eventParams.items = esd?.['products']?.map(createGA4Item) || [];
        }
        break;
      }

      case EMableEventName.LEAD: {
        // Add lead-specific parameters
        eventParams.currency = esd?.cart?.currency;
        eventParams['lead_source'] = esd?.['lead_source'] || 'website';
        eventParams.value = 1;
        break;
      }

      case EMableEventName.CUSTOM_EVENT: {
        if (esd?.['product']) {
          const product = esd?.['product'];
          eventParams.currency = product.cu;
          eventParams.value = product.pr;
          eventParams.items = [
            {
              currency: product.cu,
              item_brand: product.br,
              item_category: product.ca?.[0],
              item_category2: product.ca?.[1],
              item_category3: product.ca?.[2],
              item_category4: product.ca?.[3],
              item_category5: product.ca?.[4],
              item_id: product.id,
              item_name: product.nm,
              item_variant: product.va,
              price: product.pr,
            },
          ];
        }

        if (esd?.cart) {
          const cart = esd.cart;
          if (!eventParams.currency) {
            eventParams.currency = cart.currency;
          }
          if (!eventParams.value) {
            eventParams.value = cart.total?.price
              ? Number.parseFloat(cart.total.price.toString())
              : 0;
          }
          if (!eventParams.items) {
            eventParams.items = cart.products
              ? cart.products.map(createGA4Item)
              : mapCartItemsToGA4Items(cart);
          }
        }
        break;
      }
    }
  } catch (error) {
    Logger.error('Error transforming Mable event to GA4 event', {
      error,
      esd,
      eventName,
    });
  }

  return {
    event_name: ga4EventName,
    event_params: eventParams,
  };
}
